import { colorPalette } from "@wish-ui";
import { PAGE_URLS } from "utils/constants";
import { CAMPAIGN_THEMES } from "./campaigns";
import { CampaignTheme, HomeHeaderStyle } from "./types";

export * from "./types";

export const DEFAULT_HOME_HEADER_STYLE: HomeHeaderStyle = {
  background: colorPalette.popWhite[500],
  appearance: "light",
};

export const SPLASH_INTRO_PLAYED_SESSION_KEY = "campaignSplashIntroPlayed";

/**
 * Kill switch in the in-house feature flag service (universal feature).
 * Setting its highest-weight variant to DISABLED turns the active campaign off.
 */
export const CAMPAIGN_THEME_FEATURE = "CAMPAIGN_THEME";
const CAMPAIGN_THEME_DISABLED_VARIANT = "DISABLED";

// Cold starts land on one of these; deep links elsewhere skip the intro.
const SPLASH_INTRO_ENTRY_PATHS = ["/", "/index.html", PAGE_URLS.HOME];

export const isCampaignActive = (campaign: CampaignTheme, now: Date) => {
  const time = now.getTime();
  return (
    time >= Date.parse(campaign.activeFrom) &&
    time < Date.parse(campaign.activeTo)
  );
};

export const getActiveCampaignTheme = (
  now: Date = new Date(),
  campaigns: CampaignTheme[] = CAMPAIGN_THEMES
): CampaignTheme | null =>
  campaigns.find((campaign) => isCampaignActive(campaign, now)) ?? null;

/**
 * Fails open: a missing feature, a network error or any other variant keeps
 * the campaign on; only an explicit DISABLED variant switches it off.
 */
export const isCampaignDisabledByFlag = (variantName?: string | null) =>
  variantName === CAMPAIGN_THEME_DISABLED_VARIANT;

/**
 * The themed header is tied to the campaign hero banner: it shows while the
 * banner is in view. Without a banner the hero can collapse to zero height
 * (e.g. no commissions card), so the header stays default.
 */
export const hasThemedHomeHeader = (campaign: CampaignTheme | null) =>
  !!campaign?.slots.homeHeader && !!campaign.slots.homeHero;

export const getHomeHeaderStyle = (
  campaign: CampaignTheme | null,
  isHeroInView: boolean
): HomeHeaderStyle =>
  campaign?.slots.homeHeader && hasThemedHomeHeader(campaign) && isHeroInView
    ? campaign.slots.homeHeader
    : DEFAULT_HOME_HEADER_STYLE;

export const shouldShowSplashIntro = ({
  campaign,
  isNativeApp,
  pathname,
  hasPlayedThisSession,
}: {
  campaign: CampaignTheme | null;
  isNativeApp: boolean;
  pathname: string;
  hasPlayedThisSession: boolean;
}) =>
  !!campaign?.slots.splashIntro &&
  isNativeApp &&
  !hasPlayedThisSession &&
  SPLASH_INTRO_ENTRY_PATHS.includes(pathname);
